/* Misk Noir theme script. Vanilla JS, custom elements, no dependencies. */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const routes = theme.routes;
  const strings = theme.strings;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const mqDesktop = window.matchMedia('(min-width: 750px)');
  const mqWide = window.matchMedia('(min-width: 1100px)');
  const isRTL = () => document.documentElement.dir === 'rtl';

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

  const getFocusable = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);

  /* ---------- Toast ---------- */
  let toastTimer;
  const showToast = (message) => {
    const toast = document.getElementById('Toast');
    if (!toast || !message) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.hidden = true;
    }, 2600);
  };

  /* ---------- Modal drawer (menu, search, cart, filters) ---------- */
  const openDrawers = [];

  class ModalDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      if (this.id === 'FacetsDrawer') {
        /* On desktop the filters render as a static sidebar, not a dialog */
        const sync = () => {
          if (mqWide.matches) {
            this.close();
            this.removeAttribute('role');
            this.removeAttribute('aria-modal');
          } else {
            this.setAttribute('role', 'dialog');
            this.setAttribute('aria-modal', 'true');
          }
        };
        sync();
        if (mqWide.addEventListener) mqWide.addEventListener('change', sync);
      }
      this.addEventListener('click', (e) => {
        if (e.target === this) this.close();
      });
      this.addEventListener('keydown', (e) => this.onKeydown(e));
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      openDrawers.push(this);
      clearTimeout(ModalDrawer.clearTimer);
      document.body.dataset.drawer = this.id;
      document.body.classList.add('is-locked');
      this.setExpanded(true);
      const auto = this.querySelector('[autofocus], .psearch__input');
      window.setTimeout(() => {
        const target = auto || getFocusable(this.panel || this)[0];
        if (target) target.focus({ preventScroll: true });
      }, 60);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      const idx = openDrawers.indexOf(this);
      if (idx > -1) openDrawers.splice(idx, 1);
      if (!openDrawers.length) {
        document.body.classList.remove('is-locked');
        ModalDrawer.clearTimer = setTimeout(() => {
          if (!openDrawers.length) delete document.body.dataset.drawer;
        }, 420);
      } else {
        document.body.dataset.drawer = openDrawers[openDrawers.length - 1].id;
      }
      this.setExpanded(false);
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
    }

    setExpanded(value) {
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', String(value)));
    }

    onKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = getFocusable(this.panel || this);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('modal-drawer', ModalDrawer);

  const getDrawer = (id) => document.getElementById(id);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = getDrawer(opener.dataset.drawerOpen);
      if (drawer && typeof drawer.open === 'function') {
        if (drawer.id === 'FacetsDrawer' && mqWide.matches) return;
        e.preventDefault();
        openDrawers.slice().forEach((d) => d !== drawer && d.close());
        drawer.open(opener);
      }
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('modal-drawer');
      if (drawer) drawer.close();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openDrawers.length) openDrawers[openDrawers.length - 1].close();
  });

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      let ticking = false;
      const update = () => {
        this.classList.toggle('is-scrolled', window.scrollY > 8);
        ticking = false;
      };
      window.addEventListener(
        'scroll',
        () => {
          if (!ticking) {
            window.requestAnimationFrame(update);
            ticking = true;
          }
        },
        { passive: true }
      );
      update();

      /* Desktop dropdowns: one open at a time, close outside or on Escape */
      const dropdowns = Array.from(this.querySelectorAll('.nav__dd'));
      dropdowns.forEach((dd) => {
        dd.addEventListener('toggle', () => {
          if (dd.open) dropdowns.forEach((o) => o !== dd && (o.open = false));
        });
      });
      document.addEventListener('click', (e) => {
        dropdowns.forEach((dd) => {
          if (dd.open && !dd.contains(e.target)) dd.open = false;
        });
      });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        dropdowns.forEach((dd) => {
          if (dd.open) {
            dd.open = false;
            dd.querySelector('summary').focus();
          }
        });
      });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announce__msg'));
      if (this.items.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 4000;
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.start();
    }

    start() {
      this.stop();
      this.timer = window.setInterval(() => this.next(), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
    }

    next() {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }

    disconnectedCallback() {
      this.stop();
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Slider (hero, gallery, rails) ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
      this.buttons = Array.from(this.querySelectorAll('[data-slide-to]'));
      this.counter = this.querySelector('[data-slider-current]');
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.current = 0;
      this.autoplayDelay = parseInt(this.dataset.autoplay, 10) || 0;
      this.userStopped = false;

      this.buttons.forEach((btn) =>
        btn.addEventListener('click', () => {
          this.stopAutoplay(true);
          this.goTo(parseInt(btn.dataset.slideTo, 10));
        })
      );
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.page(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.page(1));

      if ('IntersectionObserver' in window && this.slides.length > 1) {
        this.slideObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                this.setActive(this.slides.indexOf(entry.target));
              }
            });
          },
          { root: this.track, threshold: [0.6] }
        );
        this.slides.forEach((s) => this.slideObserver.observe(s));
      }

      this.track.addEventListener('scroll', debounce(() => this.updateArrows(), 80), { passive: true });
      this.updateArrows();
      this.setActive(0, true);

      if (this.autoplayDelay > 0 && this.slides.length > 1 && !reduceMotion.matches) this.setupAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.slideObserver) this.slideObserver.disconnect();
      if (this.viewObserver) this.viewObserver.disconnect();
    }

    setActive(index, force) {
      if (index < 0 || (!force && index === this.current)) return;
      this.current = index;
      this.buttons.forEach((btn) => {
        const active = parseInt(btn.dataset.slideTo, 10) === index;
        if (active) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(index + 1);
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.querySelectorAll('[data-slide-focusable]').forEach((el) => {
          if (active) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
        slide.querySelectorAll('video.hero__video, video[autoplay]').forEach((v) => {
          if (active && !reduceMotion.matches) {
            const p = v.play();
            if (p && p.catch) p.catch(() => {});
          } else if (!active) {
            v.pause();
          }
        });
      });
    }

    goTo(index, behavior) {
      const slide = this.slides[index];
      if (!slide) return;
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const delta = isRTL() ? slideRect.right - trackRect.right : slideRect.left - trackRect.left;
      this.track.scrollBy({ left: delta, behavior: behavior || (reduceMotion.matches ? 'auto' : 'smooth') });
      this.setActive(index);
    }

    goToMedia(mediaId) {
      const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (idx > -1) this.goTo(idx);
    }

    page(dir) {
      this.stopAutoplay(true);
      const amount = this.track.clientWidth * 0.9 * dir * (isRTL() ? -1 : 1);
      this.track.scrollBy({ left: amount, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    updateArrows() {
      if (!this.prevBtn && !this.nextBtn) return;
      const pos = Math.abs(this.track.scrollLeft);
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prevBtn) this.prevBtn.disabled = pos <= 2;
      if (this.nextBtn) this.nextBtn.disabled = pos >= max;
    }

    setupAutoplay() {
      const stopForGood = () => this.stopAutoplay(true);
      this.track.addEventListener('pointerdown', stopForGood, { passive: true });
      this.track.addEventListener('touchstart', stopForGood, { passive: true });
      this.track.addEventListener('wheel', stopForGood, { passive: true });
      this.addEventListener('keydown', stopForGood);
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.resumeAutoplay());
      this.addEventListener('focusin', () => this.pauseAutoplay());
      this.addEventListener('focusout', () => this.resumeAutoplay());
      document.addEventListener('visibilitychange', () => (document.hidden ? this.pauseAutoplay() : this.resumeAutoplay()));
      if ('IntersectionObserver' in window) {
        this.viewObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? this.resumeAutoplay() : this.pauseAutoplay()));
        });
        this.viewObserver.observe(this);
      } else {
        this.resumeAutoplay();
      }
    }

    resumeAutoplay() {
      if (this.userStopped || this.autoplayDelay <= 0 || this.timer) return;
      this.timer = window.setInterval(() => {
        const next = (this.current + 1) % this.slides.length;
        this.goTo(next);
      }, this.autoplayDelay);
    }

    pauseAutoplay() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    stopAutoplay(byUser) {
      this.pauseAutoplay();
      if (byUser) this.userStopped = true;
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Reveal on scroll ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, obs) => {
            window.__mnRevealSeen = true;
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-in');
                obs.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )
      : null;

  const initReveal = (root = document) => {
    root.querySelectorAll('.reveal:not(.is-in)').forEach((el) => {
      if (!revealObserver || reduceMotion.matches) el.classList.add('is-in');
      else revealObserver.observe(el);
    });
    /* Safety net: if the observer never reports (old webviews, prerender), show everything */
    setTimeout(() => {
      if (!window.__mnRevealSeen) document.querySelectorAll('.reveal:not(.is-in)').forEach((el) => el.classList.add('is-in'));
    }, 2500);
  };

  /* ---------- Cart ---------- */
  const Cart = {
    sectionIds() {
      const ids = ['cart-drawer', 'cart-count'];
      document.querySelectorAll('[data-cart-region]').forEach((el) => {
        const id = el.dataset.cartRegion;
        if (id && !ids.includes(id)) ids.push(id);
      });
      return ids;
    },

    async request(url, body, isForm) {
      const opts = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
      if (isForm) {
        opts.body = body;
      } else {
        opts.headers['Content-Type'] = 'application/json';
        opts.body = JSON.stringify(body);
      }
      const res = await fetch(url, opts);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (typeof data.status === 'number' && data.status >= 400)) {
        const msg = data.description || data.message || strings.error;
        throw new Error(typeof msg === 'string' ? msg : strings.error);
      }
      return data;
    },

    async add(formData) {
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      const data = await this.request(`${routes.cartAdd}.js`, formData, true);
      this.render(data.sections);
      return data;
    },

    async change(key, quantity) {
      const data = await this.request(`${routes.cartChange}.js`, {
        id: key,
        quantity,
        sections: this.sectionIds(),
        sections_url: window.location.pathname,
      });
      this.render(data.sections);
      return data;
    },

    updateNote: debounce((note) => {
      fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      }).catch(() => {});
    }, 500),

    render(sections) {
      if (!sections) return;
      const active = document.activeElement;
      const focusKey = active && active.dataset ? active.dataset.key : null;
      const focusStep = active && active.dataset ? active.dataset.qtyStep : null;
      const focusInDrawer = active && active.closest && active.closest('#CartDrawer');

      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-drawer') {
          const next = doc.getElementById('CartDrawerInner');
          const current = document.getElementById('CartDrawerInner');
          if (next && current) current.innerHTML = next.innerHTML;
        } else if (id === 'cart-count') {
          const valueEl = doc.querySelector('[data-cart-count-value]');
          const count = valueEl ? parseInt(valueEl.textContent, 10) || 0 : 0;
          document.querySelectorAll('[data-cart-count]').forEach((badge) => {
            badge.textContent = String(count);
            badge.hidden = count === 0;
            badge.classList.remove('is-bump');
            void badge.offsetWidth;
            badge.classList.add('is-bump');
          });
        } else {
          const next = doc.querySelector(`[data-cart-region="${id}"]`);
          const current = document.querySelector(`[data-cart-region="${id}"]`);
          if (next && current) current.innerHTML = next.innerHTML;
        }
      });

      let target = null;
      if (focusKey) {
        const sel = focusStep
          ? `[data-key="${CSS.escape(focusKey)}"][data-qty-step="${focusStep}"]`
          : `[data-qty-input][data-key="${CSS.escape(focusKey)}"]`;
        target = document.querySelector(focusInDrawer ? `#CartDrawer ${sel}` : sel);
      }
      if (!target && focusInDrawer) target = document.querySelector('#CartDrawer .drawer__head [data-drawer-close]');
      if (target) target.focus({ preventScroll: true });
    },
  };
  window.MiskCart = Cart;

  /* Add to cart forms: product form, showcase Buy and quick add */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('[data-confirm]') && !window.confirm(form.dataset.confirm)) {
      e.preventDefault();
      return;
    }

    if (form.matches('[data-facets-form]')) {
      Array.from(form.elements).forEach((el) => {
        if (el.name && el.value === '' && el.type !== 'submit') el.disabled = true;
      });
      document.querySelectorAll('[data-sort-select]').forEach((sel) => {
        if (!sel.form && sel.name) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'sort_by';
          hidden.value = sel.value;
          form.appendChild(hidden);
        }
      });
      return;
    }

    if (!form.matches('[data-ajax-cart]')) return;
    e.preventDefault();
    const mode = form.dataset.ajaxCart;
    const button = e.submitter || form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    if (button && button.getAttribute('aria-busy') === 'true') return;
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
    }
    if (errorEl) errorEl.hidden = true;

    try {
      await Cart.add(new FormData(form));
      const drawer = getDrawer('CartDrawer');
      if (mode === 'open' && drawer && typeof drawer.open === 'function') {
        drawer.open(button);
      } else if (mode === 'open' && !drawer) {
        window.location.href = routes.cart;
      } else {
        showToast(strings.added);
        if (button && button.classList.contains('btn-round')) {
          button.classList.add('is-added');
          setTimeout(() => button.classList.remove('is-added'), 2200);
        }
      }
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      } else {
        showToast(err.message);
      }
    } finally {
      if (button) {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    }
  });

  /* Cart line quantity and remove (drawer and cart page) */
  const setLineBusy = (key) => {
    document.querySelectorAll(`.cart-line[data-key="${CSS.escape(key)}"]`).forEach((line) => line.classList.add('is-updating'));
  };

  const changeLine = async (key, qty) => {
    setLineBusy(key);
    try {
      await Cart.change(key, Math.max(0, qty));
    } catch (err) {
      showToast(err.message);
      document.querySelectorAll('.cart-line.is-updating').forEach((l) => l.classList.remove('is-updating'));
    }
  };

  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (step && step.dataset.key) {
      e.preventDefault();
      const line = step.closest('.cart-line');
      const input = line && line.querySelector('[data-qty-input]');
      const current = input ? parseInt(input.value, 10) || 0 : 0;
      changeLine(step.dataset.key, current + parseInt(step.dataset.qtyStep, 10));
      return;
    }
    const remove = e.target.closest('[data-cart-remove]');
    if (remove && remove.dataset.key) {
      e.preventDefault();
      changeLine(remove.dataset.key, 0);
    }
  });

  const onQtyInput = debounce((input) => {
    const val = parseInt(input.value, 10);
    if (!Number.isNaN(val)) changeLine(input.dataset.key, val);
  }, 400);

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (t.matches('[data-qty-input]')) {
      onQtyInput(t);
      return;
    }
    if (t.matches('[data-cart-note]')) {
      Cart.updateNote(t.value);
      return;
    }
    if (t.matches('[data-sort-select]')) {
      if (t.form) {
        t.form.requestSubmit ? t.form.requestSubmit() : t.form.submit();
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', t.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      }
      return;
    }
    const facetsForm = t.closest && t.closest('[data-facets-form]');
    if (facetsForm && mqWide.matches) {
      facetsForm.requestSubmit ? facetsForm.requestSubmit() : facetsForm.submit();
    }
  });

  document.addEventListener('input', (e) => {
    if (e.target.matches('[data-cart-note]')) Cart.updateNote(e.target.value);
  });

  /* ---------- Quantity input (product page) ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('[data-step]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const min = parseInt(this.input.min, 10) || 1;
          const val = (parseInt(this.input.value, 10) || min) + parseInt(btn.dataset.step, 10);
          this.input.value = String(Math.max(min, val));
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Product info: variants ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.url = this.dataset.url;
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.picker = this.querySelector('[data-variant-picker]');
      if (this.picker) this.picker.addEventListener('change', () => this.onVariantChange());
    }

    selectedOptions() {
      const values = [];
      this.picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        values[parseInt(input.dataset.optionIndex, 10)] = input.value;
      });
      return values;
    }

    onVariantChange() {
      const options = this.selectedOptions();
      options.forEach((val, i) => {
        const label = this.picker.querySelector(`[data-option-value="${i}"]`);
        if (label) label.textContent = val;
      });
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === options[i]));
      this.updateButtons(variant);
      if (!variant) return;

      const section = this.closest('.pdp') || document;
      section.querySelectorAll('input[data-variant-id]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      document.querySelectorAll('.satc [data-variant-id]').forEach((input) => (input.value = variant.id));

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      if (variant.featured_media) {
        const slider = this.querySelector('.gallery');
        if (slider && slider.goToMedia) slider.goToMedia(variant.featured_media.id);
      }
      this.renderPrice(variant.id);
    }

    updateButtons(variant) {
      const available = !!(variant && variant.available);
      const label = !variant ? strings.unavailable : available ? strings.addToCart : strings.soldOut;
      document.querySelectorAll('[data-add-button], [data-sticky-add]').forEach((btn) => {
        btn.disabled = !available;
        const span = btn.querySelector('[data-add-label]');
        if (span && btn.hasAttribute('data-add-button')) span.textContent = label;
        else if (span && !available) span.textContent = label;
      });
    }

    async renderPrice(variantId) {
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      try {
        const res = await fetch(`${this.url}?variant=${variantId}&section_id=${this.sectionId}`, { signal: this.abort.signal });
        const doc = parseHTML(await res.text());
        ['[data-price-region]', '[data-sticky-price]'].forEach((sel) => {
          const next = doc.querySelector(sel);
          const current = document.querySelector(sel);
          if (next && current) current.innerHTML = next.innerHTML;
        });
        const nextSticky = doc.querySelector('[data-sticky-add] [data-add-label]');
        const currentSticky = document.querySelector('[data-sticky-add] [data-add-label]');
        if (nextSticky && currentSticky) currentSticky.textContent = nextSticky.textContent;
      } catch (err) {
        /* aborted or offline: keep current price */
      }
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ---------- Sticky add to cart (mobile) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.target = document.getElementById(this.dataset.target);
      this.button = this.querySelector('[data-sticky-add]');
      if (!this.target || !('IntersectionObserver' in window)) return;
      this.button.addEventListener('click', () => {
        const form = document.getElementById(this.dataset.formId);
        if (!form) return;
        if (form.requestSubmit) form.requestSubmit(this.target);
        else this.target.click();
      });
      this.observer = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.toggle(past && mqMobile.matches);
      });
      this.observer.observe(this.target);
    }

    toggle(show) {
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      if (this.button) this.button.tabIndex = show ? 0 : -1;
      document.documentElement.style.setProperty('--satc-h', show ? `${this.offsetHeight}px` : '0px');
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.documentElement.style.setProperty('--satc-h', '0px');
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      this.input.addEventListener(
        'input',
        debounce(() => this.search(), 250)
      );
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        this.classList.remove('has-results');
        return;
      }
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,page',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][prefix]': 'last',
        section_id: 'predictive-search',
      });
      try {
        const res = await fetch(`${routes.predictiveSearch}?${params.toString()}`, { signal: this.abort.signal });
        if (!res.ok) throw new Error(String(res.status));
        const doc = parseHTML(await res.text());
        const content = doc.querySelector('[data-predictive-content]');
        this.results.innerHTML = content ? content.outerHTML : '';
        this.classList.toggle('has-results', !!content);
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.results.innerHTML = '';
          this.classList.remove('has-results');
        }
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const doc = parseHTML(await res.text());
          const next = doc.querySelector('product-recommendations');
          if (next && next.innerHTML.trim().length) this.innerHTML = next.innerHTML;
          else this.remove();
        } catch (err) {
          /* ignore */
        }
      };
      if ('IntersectionObserver' in window) {
        const obs = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              obs.disconnect();
              load();
            }
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        obs.observe(this);
      } else {
        load();
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Footer accordions open on desktop ---------- */
  const syncFooter = () => {
    document.querySelectorAll('[data-desktop-open]').forEach((d) => {
      d.open = mqDesktop.matches;
    });
  };
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('[data-desktop-open] > summary');
    if (summary && mqDesktop.matches) e.preventDefault();
  });
  if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', syncFooter);

  /* ---------- Reduced motion: pause autoplay videos ---------- */
  const syncVideos = () => {
    if (!reduceMotion.matches) return;
    document.querySelectorAll('video[autoplay]').forEach((v) => {
      v.pause();
      v.removeAttribute('autoplay');
    });
  };

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    syncFooter();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest && e.target.closest('[data-slide]');
    const slider = slide && slide.closest('slider-component');
    if (slider && slider.slides) {
      slider.stopAutoplay(true);
      slider.goTo(slider.slides.indexOf(slide), 'auto');
    }
  });
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector && e.target.querySelector('#CartDrawer')) {
      const drawer = getDrawer('CartDrawer');
      if (drawer) drawer.open();
    }
  });

  /* ---------- Init ---------- */
  const init = () => {
    window.MiskReady = true;
    initReveal();
    syncFooter();
    syncVideos();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
